import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { identifySentryUser } from "@/instrument";

const AuthContext = createContext(null);

/**
 * AuthContext — memory-only session state.
 *
 * The authoritative session token lives in the httpOnly `access_token`
 * cookie set by the backend on login / register / reset. JS never reads it
 * directly (XSS-hardened). The `token` we expose here is a short-lived
 * ticket obtained from `POST /api/auth/ws-ticket` — used only for the
 * WebSocket URL param (WS can't send Authorization headers cleanly).
 * Direct fetch()/axios calls all use cookie auth via `withCredentials`.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [wsTicket, setWsTicket] = useState(null); // short-lived WS ticket, memory-only
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      identifySentryUser(data.user);
    } catch {
      setUser(false);
      setWsTicket(null);
      identifySentryUser(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      identifySentryUser(data.user);
      setWsTicket(null); // will be minted on next WS connect
      setError(null);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = apiErrorMessage(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      identifySentryUser(data.user);
      setWsTicket(null);
      setError(null);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = apiErrorMessage(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { if (typeof console !== 'undefined') console.debug('contexts/AuthContext.jsx: swallowed', err); }
    setUser(false);
    setWsTicket(null);
    identifySentryUser(null);
  };

  /**
   * Ask the backend for a 60-second WebSocket ticket. Cached on the context
   * so multiple WS connects within the same window reuse the same ticket.
   * Callers (RealtimeContext) refresh when the WS closes for auth reasons.
   */
  const getWsTicket = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/ws-ticket");
      setWsTicket(data.token);
      return data.token;
    } catch {
      setWsTicket(null);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user, error, login, register, logout, refresh,
      getWsTicket, wsTicket,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
